<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ArrowLeft, Check, Layers, Plus, Trash2, X } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { useProcurementApi } from '../composables/useProcurementApi'
import { PURCHASE_ORDER_STATUSES, baseQuantity, baseUomName } from '../types/procurement'
import ProductPickerModal from '../components/ProductPickerModal.vue'
import AgentPickerModal from '../components/AgentPickerModal.vue'
import QuantityOffcanvas from '../components/QuantityOffcanvas.vue'
import type { AgentOption, CurrencyOption, ProductOption, ProductUom, QuantityEntry } from '../types/procurement'

interface LineDraft {
  product_id: number
  product_name: string
  sku: string | null
  unit: string | null
  /** Main UOM name (the product `unit`), shown next to the product. */
  main_uom: string
  /** Additional UOMs (`GET /products/{id}/uoms` → `items[]`); empty when none. */
  uoms: ProductUom[]
  /** Multi-UOM quantity breakdown; the quantity sent is its base total. */
  quantities: QuantityEntry[]
  price: number | string
}

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const api = useProcurementApi()

const editId = computed(() => (route.params.id ? Number(route.params.id) : null))
const isEdit = computed(() => editId.value !== null)

const currencies = ref<CurrencyOption[]>([])
const loading = ref(true)
const error = ref('')
const saving = ref(false)
const saveError = ref('')
const showPicker = ref(false)
const showSupplierPicker = ref(false)
const showWarehousePicker = ref(false)
const supplierName = ref('')
const warehouseName = ref('')

const form = reactive({
  supplier_agent_id: '',
  destination_warehouse_agent_id: '',
  currency_code: '',
  exchange_rate: 1 as number | string,
  status: 'draft',
  expected_date: '',
  notes: '',
})

const lines = ref<LineDraft[]>([])

const statuses = PURCHASE_ORDER_STATUSES

function formatQty(value: number): string {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })
}

/** Base-unit quantity of a line, converted from its multi-UOM breakdown. */
function lineBaseQuantity(line: LineDraft): number {
  return baseQuantity(line.quantities, line.uoms)
}

const totalAmount = computed(() =>
  lines.value.reduce((total, line) => total + lineBaseQuantity(line) * Number(line.price || 0), 0),
)

/* Excel-like keyboard navigation between the quantity and price cells. */
const tableRef = ref<HTMLTableElement | null>(null)

type CellColumn = 'q' | 'p'

function focusCell(row: number, column: CellColumn) {
  const target = tableRef.value?.querySelector<HTMLInputElement>(`[data-cell="${column}:${row}"]`)
  if (!target) return

  target.focus()
  target.select()
}

function onCellKeydown(event: KeyboardEvent, row: number, column: CellColumn) {
  const input = event.target as HTMLInputElement
  const start = input.selectionStart ?? 0
  const end = input.selectionEnd ?? 0
  const other: CellColumn = column === 'q' ? 'p' : 'q'

  switch (event.key) {
    case 'Enter':
    case 'ArrowDown':
      event.preventDefault()
      focusCell(row + 1, column)
      break
    case 'ArrowUp':
      event.preventDefault()
      focusCell(row - 1, column)
      break
    case 'ArrowRight':
      // Only leave the cell when the caret is already at the end.
      if (start !== end || end !== input.value.length) break
      event.preventDefault()
      focusCell(row, other)
      break
    case 'ArrowLeft':
      if (start !== end || start !== 0) break
      event.preventDefault()
      focusCell(row, other)
      break
  }
}

function money(value: number, currency: string | null | undefined): string {
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))

  return currency ? `${formatted} ${currency}` : formatted
}

/** Load the product's additional UOMs; row 0 stays the main UOM. */
async function loadLineUoms(line: LineDraft) {
  try {
    const { unit, items } = await api.getProductUoms(line.product_id)
    line.uoms = items
    line.unit = unit ?? line.unit
    line.main_uom = baseUomName(items, unit ?? line.unit)

    // Row 0 is the main UOM: an empty code means factor 1 (main-unit quantity).
    const currentQuantity = line.quantities[0]?.quantity ?? ''
    line.quantities = [{ uom_code: '', quantity: currentQuantity }]
  } catch {
    line.main_uom = line.main_uom || (line.unit ?? '')
  }
}

/** Multi-UOM detail editor target; `null` when the offcanvas is closed. */
const editingLine = ref<number | null>(null)

const editing = computed(() => (editingLine.value === null ? null : (lines.value[editingLine.value] ?? null)))

function openQuantityEditor(index: number) {
  editingLine.value = index
}

function closeQuantityEditor() {
  editingLine.value = null
}

/** Replace the line's breakdown with the offcanvas result. */
function applyQuantities(quantities: QuantityEntry[]) {
  const index = editingLine.value
  if (index === null) return

  const line = lines.value[index]
  if (!line) return

  line.quantities = quantities.length > 0 ? quantities : [{ uom_code: '', quantity: '' }]
  closeQuantityEditor()
}

/** `40 pair · 2 Box` — the compact breakdown summary under the cell input. */
function breakdownLabel(line: LineDraft): string {
  return line.quantities
    .map((entry) => {
      const name = entry.uom_code
        ? (line.uoms.find((uom) => uom.uom_code === entry.uom_code)?.label || entry.uom_code)
        : line.main_uom

      return `${formatQty(Number(entry.quantity || 0))} ${name}`
    })
    .join(' · ')
}

function addProduct(product: ProductOption) {
  const existing = lines.value.find((line) => line.product_id === product.id)
  if (existing) {
    if (existing.quantities.length === 0) {
      existing.quantities.push({ uom_code: '', quantity: 0 })
    }
    existing.quantities[0].quantity = Number(existing.quantities[0].quantity || 0) + 1
    showPicker.value = false
    return
  }

  const line: LineDraft = {
    product_id: product.id,
    product_name: product.name,
    sku: product.sku,
    unit: product.unit,
    main_uom: product.unit ?? '',
    uoms: [],
    quantities: [{ uom_code: '', quantity: 1 }],
    price: 0,
  }

  lines.value.push(line)
  showPicker.value = false
  void loadLineUoms(line)
}

function removeLine(index: number) {
  lines.value.splice(index, 1)
}

function validate(): string {
  if (!form.supplier_agent_id) return t('procurement.form.validationSupplier')
  if (!form.currency_code) return t('procurement.form.validationCurrency')
  if (lines.value.length === 0) return t('procurement.form.validationItems')

  for (const line of lines.value) {
    if (!line.product_id) return t('procurement.form.validationProduct')
    if (lineBaseQuantity(line) <= 0) return t('procurement.form.validationQuantity')
    if (Number(line.price) <= 0) return t('procurement.form.validationPrice')
  }

  return ''
}

async function loadOptions() {
  const currencyList = await api.listCurrencies().catch(() => [])
  currencies.value = currencyList

  if (!isEdit.value && currencyList.length > 0 && !form.currency_code) {
    form.currency_code = currencyList[0].code
  }
}

function pickSupplier(agent: AgentOption) {
  form.supplier_agent_id = String(agent.id)
  supplierName.value = agent.name
  showSupplierPicker.value = false
}

function pickWarehouse(agent: AgentOption) {
  form.destination_warehouse_agent_id = String(agent.id)
  warehouseName.value = agent.name
  showWarehousePicker.value = false
}

function clearWarehouse() {
  form.destination_warehouse_agent_id = ''
  warehouseName.value = ''
}

async function loadOrder() {
  if (!editId.value) return

  const order = await api.getPurchaseOrder(editId.value)
  form.supplier_agent_id = String(order.supplier_agent_id)
  supplierName.value = order.supplier_name ?? `#${order.supplier_agent_id}`
  form.destination_warehouse_agent_id = order.destination_warehouse_agent_id ? String(order.destination_warehouse_agent_id) : ''
  warehouseName.value = order.destination_warehouse_name
    ?? (order.destination_warehouse_agent_id ? `#${order.destination_warehouse_agent_id}` : '')
  form.currency_code = order.currency_code ?? ''
  form.exchange_rate = order.exchange_rate
  form.status = String(order.status)
  form.expected_date = order.expected_date ? order.expected_date.slice(0, 10) : ''
  form.notes = order.notes ?? ''
  lines.value = (order.items ?? []).map((item) => ({
    product_id: item.product_id,
    product_name: item.product_name || `#${item.product_id}`,
    sku: item.sku,
    unit: null,
    main_uom: '',
    uoms: [] as ProductUom[],
    quantities: [{ uom_code: '', quantity: item.quantity }] as QuantityEntry[],
    price: item.price,
  }))
  await Promise.all(lines.value.map((line) => loadLineUoms(line)))
}

onMounted(async () => {
  loading.value = true
  error.value = ''
  try {
    await Promise.all([loadOptions(), loadOrder()])
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
})

async function submit() {
  saveError.value = validate()
  if (saveError.value) return

  const requesterId = auth.user?.id
  if (!requesterId) {
    saveError.value = t('procurement.form.missingUser')
    return
  }

  const payload = {
    supplier_agent_id: Number(form.supplier_agent_id),
    requester_user_id: requesterId,
    destination_warehouse_agent_id: form.destination_warehouse_agent_id ? Number(form.destination_warehouse_agent_id) : null,
    currency_code: form.currency_code,
    exchange_rate: Number(form.exchange_rate) || 1,
    status: form.status as (typeof PURCHASE_ORDER_STATUSES)[number],
    expected_date: form.expected_date || null,
    notes: form.notes.trim() || null,
    items: lines.value.map((line) => ({
      product_id: line.product_id,
      quantity: lineBaseQuantity(line),
      price: Number(line.price),
    })),
  }

  saving.value = true
  try {
    const order = isEdit.value && editId.value
      ? await api.updatePurchaseOrder(editId.value, payload)
      : await api.createPurchaseOrder(payload)

    await router.push(`/purchase-orders/${order.id}`)
  } catch (cause) {
    saveError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="page-heading">
    <div>
      <RouterLink class="back" to="/purchase-orders">
        <ArrowLeft :size="14" aria-hidden="true" />
        {{ t('procurement.form.back') }}
      </RouterLink>
      <h2>{{ isEdit ? t('procurement.form.editTitle', { id: editId }) : t('procurement.form.createTitle') }}</h2>
    </div>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('procurement.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <RouterLink class="btn-primary" to="/purchase-orders">
      <ArrowLeft :size="15" aria-hidden="true" />
      {{ t('procurement.form.back') }}
    </RouterLink>
  </div>

  <form v-else class="po-form" @submit.prevent="submit">
    <div class="po-form__grid">
      <div class="po-form__col">
        <section class="panel po-form__section">
          <h3>{{ t('procurement.form.heading') }}</h3>

          <div class="field">
            <label>{{ t('procurement.form.supplier') }}</label>
            <button class="field-button" type="button" @click="showSupplierPicker = true">
              <span>{{ supplierName || t('procurement.form.selectSupplier') }}</span>
              <span aria-hidden="true">▾</span>
            </button>
          </div>

          <div class="field">
            <label>{{ t('procurement.form.warehouse') }} <small>{{ t('procurement.common.optional') }}</small></label>
            <button class="field-button" type="button" @click="showWarehousePicker = true">
              <span>{{ warehouseName || t('procurement.form.selectWarehouse') }}</span>
              <span aria-hidden="true">▾</span>
            </button>
            <small>
              {{ t('procurement.form.warehouseHint') }}
              <button v-if="form.destination_warehouse_agent_id" class="link-action" type="button" @click="clearWarehouse">
                <X :size="12" aria-hidden="true" />
                {{ t('procurement.common.clear') }}
              </button>
            </small>
          </div>

          <div class="field-inline">
            <div class="field">
              <label>{{ t('procurement.form.currency') }}</label>
              <select v-model="form.currency_code" required>
                <option value="">{{ t('procurement.common.selectPlaceholder') }}</option>
                <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
                  {{ currency.code }}<template v-if="currency.name"> · {{ currency.name }}</template>
                </option>
              </select>
            </div>
            <div class="field">
              <label>{{ t('procurement.form.exchangeRate') }}</label>
              <input v-model="form.exchange_rate" type="number" min="0.00000001" step="0.00000001">
            </div>
          </div>

          <div class="field-inline">
            <div class="field">
              <label>{{ t('procurement.form.expectedDate') }}</label>
              <input v-model="form.expected_date" type="date">
            </div>
            <div class="field">
              <label>{{ t('procurement.form.status') }}</label>
              <select v-model="form.status">
                <option v-for="status in statuses" :key="status" :value="status">
                  {{ t(`procurement.status.${status}`) }}
                </option>
              </select>
            </div>
          </div>

          <div class="field">
            <label>{{ t('procurement.form.notes') }} <small>{{ t('procurement.common.optional') }}</small></label>
            <textarea v-model="form.notes" rows="2" :placeholder="t('procurement.form.notesPlaceholder')" />
          </div>

          <p class="form-note">{{ t('procurement.form.requesterNote', { name: auth.user?.name ?? '—' }) }}</p>
        </section>

        <small v-if="saveError" class="error-inline">{{ saveError }}</small>

        <div class="form-actions">
          <button class="btn-primary" type="submit" :disabled="saving">
            <Check v-if="!saving" :size="15" aria-hidden="true" />
            {{ saving ? t('procurement.common.saving') : (isEdit ? t('procurement.form.update') : t('procurement.form.create')) }}
          </button>
          <RouterLink class="btn-secondary" to="/purchase-orders">
            <X :size="15" aria-hidden="true" />
            {{ t('procurement.common.cancel') }}
          </RouterLink>
        </div>
      </div>

      <section class="panel po-form__section po-form__section--items">
        <div class="panel-header">
          <h3>{{ t('procurement.form.lines') }}</h3>
          <button class="btn-secondary" type="button" @click="showPicker = true">
            <Plus :size="15" aria-hidden="true" />
            {{ t('procurement.form.addLine') }}
          </button>
        </div>

        <table v-if="lines.length" ref="tableRef" class="data-table sheet">
          <thead>
            <tr>
              <th>{{ t('procurement.form.product') }}</th>
              <th class="cell-qty">{{ t('procurement.form.quantity') }}</th>
              <th class="cell-qty">{{ t('procurement.form.pricePerBase') }}</th>
              <th class="cell-qty">{{ t('procurement.form.lineTotal') }}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, index) in lines" :key="`${line.product_id}-${index}`">
              <td class="cell-name">
                {{ line.product_name }}
                <small class="cell-sub">
                  {{ line.sku || '—' }}
                  <template v-if="line.main_uom"> · {{ line.main_uom }}</template>
                </small>
              </td>
              <td>
                <div class="sheet-qty">
                  <div v-if="line.quantities.length <= 1" class="uom-group">
                    <input
                      v-model="line.quantities[0].quantity"
                      class="sheet-cell"
                      :data-cell="`q:${index}`"
                      type="number"
                      min="0"
                      step="any"
                      @keydown="onCellKeydown($event, index, 'q')"
                    >
                    <span class="uom-addon uom-addon--static">{{ line.main_uom || '—' }}</span>
                  </div>
                  <div v-else class="sheet-total">
                    <strong>{{ formatQty(lineBaseQuantity(line)) }}</strong>
                    <span>{{ line.main_uom || '—' }}</span>
                  </div>
                  <button
                    class="sheet-uom"
                    type="button"
                    :title="t('procurement.form.detailedUom')"
                    @click="openQuantityEditor(index)"
                  >
                    <Layers :size="14" aria-hidden="true" />
                    <span v-if="line.quantities.length > 1" class="sheet-uom__count">{{ line.quantities.length }}</span>
                  </button>
                </div>
                <small v-if="line.quantities.length > 1" class="cell-sub">
                  {{ breakdownLabel(line) }}
                </small>
              </td>
              <td>
                <input
                  v-model="line.price"
                  class="sheet-cell"
                  :data-cell="`p:${index}`"
                  type="number"
                  min="0"
                  step="0.01"
                  @keydown="onCellKeydown($event, index, 'p')"
                >
              </td>
              <td class="cell-qty">{{ money(lineBaseQuantity(line) * Number(line.price || 0), form.currency_code) }}</td>
              <td class="sheet-actions">
                <button class="link-action danger" type="button" @click="removeLine(index)">
                  <Trash2 :size="14" aria-hidden="true" />
                  {{ t('procurement.form.removeLine') }}
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="3" class="cell-qty"><strong>{{ t('procurement.form.totalAmount') }}</strong></td>
              <td class="cell-qty"><strong>{{ money(totalAmount, form.currency_code) }}</strong></td>
              <td />
            </tr>
          </tfoot>
        </table>

        <p v-else class="empty-inline">{{ t('procurement.form.validationItems') }}</p>
      </section>
    </div>
  </form>

  <ProductPickerModal :open="showPicker" @close="showPicker = false" @select="addProduct" />
  <AgentPickerModal
    :open="showSupplierPicker"
    role="supplier"
    :title="t('procurement.form.supplier')"
    :selected-id="form.supplier_agent_id ? Number(form.supplier_agent_id) : null"
    @close="showSupplierPicker = false"
    @select="pickSupplier"
  />
  <AgentPickerModal
    :open="showWarehousePicker"
    role="warehouse"
    :title="t('procurement.form.warehouse')"
    :selected-id="form.destination_warehouse_agent_id ? Number(form.destination_warehouse_agent_id) : null"
    @close="showWarehousePicker = false"
    @select="pickWarehouse"
  />
  <QuantityOffcanvas
    :open="editingLine !== null"
    :product-name="editing?.product_name ?? ''"
    :sku="editing?.sku ?? null"
    :main-uom="editing?.main_uom ?? ''"
    :uoms="editing?.uoms ?? []"
    :quantities="editing?.quantities ?? []"
    @close="closeQuantityEditor"
    @apply="applyQuantities"
  />
</template>

<style scoped>
/* Two columns: order details + actions on the left, the product lines on the
   right. `min-width: 0` on every grid item stops long select/table content
   from overflowing its track and overlapping the neighbouring column. */
.po-form__grid {
  display: grid;
  grid-template-columns: minmax(300px, 380px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}
.po-form__col { display: grid; gap: 12px; align-content: start; min-width: 0; }
.po-form__section { display: grid; gap: 12px; align-content: start; min-width: 0; }
.po-form__section > h3 { margin: 0; }
.po-form__section--items { overflow-x: auto; }
.po-form__section .field { min-width: 0; }
.po-form__section .field input,
.po-form__section .field select,
.po-form__section .field textarea,
.po-form__section .field-button { width: 100%; max-width: 100%; min-width: 0; }
.field-button > span:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.field-inline { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 12px; min-width: 0; }
/* Excel-like order lines: tight cells with borderless inputs that fill the cell. */
.sheet { table-layout: fixed; border-radius: 0; }
.sheet th { padding: 6px 8px; white-space: normal; }
.sheet th:nth-child(1) { width: auto; }
.sheet th:nth-child(2) { width: 210px; }
.sheet th:nth-child(3) { width: 150px; }
.sheet th:nth-child(4) { width: 130px; }
.sheet th:nth-child(5) { width: 120px; }
.sheet td { padding: 0; vertical-align: middle; }
.sheet tfoot td { padding: 8px 9px; }
.sheet .cell-name { padding: 8px 9px; }
.sheet .sheet-actions { padding: 8px; text-align: center; white-space: nowrap; }
.sheet-cell {
  width: 100%;
  min-width: 0;
  padding: 8px 9px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 13px;
  text-align: right;
}
.sheet-cell:focus {
  outline: 2px solid #765a30;
  outline-offset: -2px;
  background: #fffdf5;
}
.sheet-qty { display: flex; align-items: stretch; min-width: 0; }
.sheet-qty .uom-group { display: flex; flex: 1 1 auto; align-items: stretch; min-width: 0; }
.sheet-total {
  display: flex;
  flex: 1 1 auto;
  align-items: baseline;
  justify-content: flex-end;
  gap: 6px;
  min-width: 0;
  padding: 8px 9px;
}
.sheet-total strong { color: #2b2620; font-size: 14px; font-weight: 700; }
.sheet-total span { color: #6f675c; font-size: 12px; }
.sheet .uom-addon {
  display: inline-flex;
  align-items: center;
  max-width: 110px;
  padding: 0 8px;
  border: 0;
  border-left: 1px solid #e2e8ee;
  background: #f7f9fb;
  color: #5c5449;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.sheet .uom-addon--static { pointer-events: none; }
.sheet-uom {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  border: 0;
  border-left: 1px solid #eef1f4;
  background: #fff;
  color: #765a30;
  cursor: pointer;
}
.sheet-uom:hover { background: #faf7f2; }
.sheet-uom__count {
  position: absolute;
  top: 3px;
  right: 3px;
  min-width: 14px;
  padding: 0 3px;
  border-radius: 999px;
  background: #765a30;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
}
.back { display: inline-flex; align-items: center; gap: 6px; }

@media (max-width: 900px) {
  .po-form__grid { grid-template-columns: minmax(0, 1fr); }
  .field-inline { grid-template-columns: minmax(0, 1fr); }
}
</style>
